import type { ApolloClient } from "@apollo/client";
import type { DeepPartial } from "@apollo/client/utilities";
import { hexToBytes } from "@noble/hashes/utils.js";
import type { SendNewMessageRequest } from "deso-protocol";
import { decrypt } from "deso-protocol";
import {
  createUserAssociation,
  decryptChatMessage,
  identity,
} from "deso-protocol";
import type { TypeWithOptionalFeesAndExtraData } from "deso-protocol/src/types";

import type {
  Account,
  FilterableMessageThreadFragment,
  FocusSubscription,
  Maybe,
  MessageFragment,
  ThreadFragment,
  ThreadMemberFragment,
} from "@/graphql/codegen/graphql";
import { AccessGroupByAccessGroupOwnerPublicKeyAndAccessGroupKeyNameDocument } from "@/graphql/codegen/graphql";
import {
  DEFAULT_MESSAGING_GROUP_KEY_NAME,
  DESO_TOKEN_NAME,
} from "@/utils/constants";
import { getPollInterval, parseError } from "@/utils/helpers";
import type { AudioMetadata, VideoMetadata } from "@/utils/media-utils";
import type { PartialAccount, PostMediaMeta } from "@/utils/types";
import { getUserDisplayName, getUserName } from "@/utils/usernames";

const MESSAGE_GROUP_NAME_CONTROL_CHAR_REGEX = /[\u0000-\u001F\u007F]+/g;
const MESSAGE_GROUP_NAME_ESCAPED_CONTROL_CHAR_REGEX =
  /\\u00(?:0[0-9a-f]|1[0-9a-f]|7f)/gi;
const MESSAGE_GROUP_NAME_WHITESPACE_REGEX = /\s+/g;
const CHAT_ON_EXTRA_DATA_PREFIX = "msg:";
const CHAT_ON_TYPE_KEY = "msg:type";
const CHAT_ON_IMAGE_URL_KEY = "msg:imageUrl";
const CHAT_ON_GIF_URL_KEY = "msg:gifUrl";
const CHAT_ON_GIF_TITLE_KEY = "msg:gifTitle";
const CHAT_ON_VIDEO_URL_KEY = "msg:videoUrl";
const CHAT_ON_AUDIO_URL_KEY = "msg:audioUrl";
const CHAT_ON_MEDIA_WIDTH_KEY = "msg:mediaWidth";
const CHAT_ON_MEDIA_HEIGHT_KEY = "msg:mediaHeight";
const CHAT_ON_DURATION_KEY = "msg:duration";
const CHAT_ON_FILE_NAME_KEY = "msg:fileName";
const CHAT_ON_FILE_URL_KEY = "msg:fileUrl";
const CHAT_ON_FILE_DESCRIPTION_KEY = "msg:fileDescription";
const CHAT_ON_OG_TITLE_KEY = "msg:ogTitle";
const CHAT_ON_SYSTEM_ACTION_KEY = "msg:systemAction";
const HEX_STRING_REGEX = /^[0-9a-f]+$/i;
const CHAT_ON_FILE_NAME_REGEX = /\.\w{2,5}$/;
const CHAT_ON_MEDIA_URL_REGEX = /^https?:\/\//i;
const CHAT_ON_DEFAULT_GIF_TEXT = "GIF";
const CHAT_ON_DEFAULT_VIDEO_TEXT = "video";
const CHAT_ON_DEFAULT_AUDIO_TITLE = "Audio message";

export type ChatOnData = Record<string, string>;
export type ChatOnMessageType =
  | "text"
  | "image"
  | "gif"
  | "sticker"
  | "video"
  | "audio"
  | "file"
  | "reaction"
  | "tip"
  | "system";

export interface ResolvedChatOnMessageContent {
  type: ChatOnMessageType | null;
  renderText: string;
  captionText: string;
  imageURLs: string[];
  videoURLs: string[];
  audioURLs: string[];
  mediaMeta: PostMediaMeta;
}

type ChatOnDecryptionContext =
  | {
      mode?: "none";
    }
  | {
      mode: "unencrypted";
    }
  | {
      mode: "shared-private-key";
      messagingPrivateKey: string;
    }
  | {
      mode: "chat";
      decryptionSeedHex: string;
      publicDecryptionKey: string;
    };

const isLikelyEncodedChatOnValue = (value: string) => {
  return (
    value.length >= 32 && value.length % 2 === 0 && HEX_STRING_REGEX.test(value)
  );
};

const decodeUnencryptedChatOnValue = (value: string) => {
  if (!isLikelyEncodedChatOnValue(value)) {
    return value;
  }

  try {
    return new TextDecoder().decode(hexToBytes(value));
  } catch {
    return value;
  }
};

const decryptChatOnValue = async (
  value: string,
  context: ChatOnDecryptionContext,
) => {
  if (!isLikelyEncodedChatOnValue(value)) {
    return value;
  }

  if (context.mode === "unencrypted") {
    return decodeUnencryptedChatOnValue(value);
  }

  if (context.mode === "shared-private-key") {
    try {
      return await decrypt(context.messagingPrivateKey, value);
    } catch {
      return value;
    }
  }

  if (context.mode === "chat") {
    try {
      return await decryptChatMessage(
        context.decryptionSeedHex,
        context.publicDecryptionKey,
        value,
      );
    } catch {
      try {
        return await decrypt(context.decryptionSeedHex, value);
      } catch {
        return value;
      }
    }
  }

  return value;
};

export const resolveChatOnData = async (
  message: DeepPartial<MessageFragment>,
  context: ChatOnDecryptionContext = { mode: "none" },
): Promise<ChatOnData> => {
  const entries = Object.entries(message.extraData ?? {}).filter(([key]) =>
    key.startsWith(CHAT_ON_EXTRA_DATA_PREFIX),
  );

  if (entries.length === 0) {
    return {};
  }

  const resolvedEntries = await Promise.all(
    entries.map(async ([key, value]) => {
      const resolvedValue =
        typeof value === "string"
          ? await decryptChatOnValue(value, context)
          : String(value);

      return [key, resolvedValue] as const;
    }),
  );

  return Object.fromEntries(resolvedEntries);
};

const isChatOnMediaUrl = (value?: string) => {
  return !!value && CHAT_ON_MEDIA_URL_REGEX.test(value);
};

const parseChatOnNumber = (value?: string) => {
  if (!value) {
    return undefined;
  }

  const parsed = Number(value);

  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const getChatOnMediaMetaKey = (url: string) => {
  return `chaton:${url.slice(-18)}`;
};

const getChatOnMediaOrientation = (
  width?: number,
  height?: number,
): "landscape" | "portrait" => {
  return (width ?? 0) >= (height ?? 0) ? "landscape" : "portrait";
};

const getChatOnFileFallbackText = (text: string, chatOn: ChatOnData) => {
  if (text.trim()) {
    return text;
  }

  const fileDescription = chatOn[CHAT_ON_FILE_DESCRIPTION_KEY];
  const fileName = chatOn[CHAT_ON_FILE_NAME_KEY];
  const fileUrl = chatOn[CHAT_ON_FILE_URL_KEY];
  const ogTitle = chatOn[CHAT_ON_OG_TITLE_KEY];

  if (fileDescription?.trim()) {
    return fileDescription;
  }

  if (fileUrl && fileName) {
    return `[${fileName}](${fileUrl})`;
  }

  return fileName || ogTitle || fileUrl || "";
};

const getChatOnMessageType = (chatOn: ChatOnData): ChatOnMessageType | null => {
  const type = chatOn[CHAT_ON_TYPE_KEY];

  switch (type) {
    case "text":
    case "image":
    case "gif":
    case "sticker":
    case "video":
    case "audio":
    case "file":
    case "reaction":
    case "tip":
    case "system":
      return type;
    default:
      break;
  }

  if (isChatOnMediaUrl(chatOn[CHAT_ON_GIF_URL_KEY])) {
    return "gif";
  }

  if (isChatOnMediaUrl(chatOn[CHAT_ON_IMAGE_URL_KEY])) {
    return "image";
  }

  if (isChatOnMediaUrl(chatOn[CHAT_ON_VIDEO_URL_KEY])) {
    return "video";
  }

  if (isChatOnMediaUrl(chatOn[CHAT_ON_AUDIO_URL_KEY])) {
    return "audio";
  }

  return null;
};

const getChatOnVideoMediaMeta = (
  url: string,
  width?: number,
  height?: number,
  duration?: number,
): PostMediaMeta => {
  const safeWidth = Math.round(width ?? 1280);
  const safeHeight = Math.round(height ?? 720);

  return {
    [getChatOnMediaMetaKey(url)]: {
      duration: duration ?? 0,
      height: safeHeight,
      orientation: getChatOnMediaOrientation(safeWidth, safeHeight),
      size: 0,
      thumbnailDataURL: "",
      type: "video",
      url,
      width: safeWidth,
    } satisfies VideoMetadata,
  };
};

const getChatOnAudioMediaMeta = (
  url: string,
  duration?: number,
): PostMediaMeta => {
  return {
    [getChatOnMediaMetaKey(url)]: {
      duration: duration ?? 0,
      size: 0,
      title: CHAT_ON_DEFAULT_AUDIO_TITLE,
      type: "audio",
      url,
    } satisfies AudioMetadata,
  };
};

export const resolveChatOnMessageContent = ({
  audioURLs = [],
  chatOn = {},
  imageURLs = [],
  text,
  videoURLs = [],
}: {
  text: string;
  imageURLs?: string[];
  videoURLs?: string[];
  audioURLs?: string[];
  chatOn?: ChatOnData;
}): ResolvedChatOnMessageContent => {
  const type = getChatOnMessageType(chatOn);
  const width = parseChatOnNumber(chatOn[CHAT_ON_MEDIA_WIDTH_KEY]);
  const height = parseChatOnNumber(chatOn[CHAT_ON_MEDIA_HEIGHT_KEY]);
  const duration = parseChatOnNumber(chatOn[CHAT_ON_DURATION_KEY]);

  if (!type) {
    return {
      type: null,
      renderText: text,
      captionText: "",
      imageURLs: [],
      videoURLs: [],
      audioURLs: [],
      mediaMeta: {},
    };
  }

  switch (type) {
    case "image": {
      const resolvedImageURLs =
        imageURLs.length > 0
          ? imageURLs
          : isChatOnMediaUrl(chatOn[CHAT_ON_IMAGE_URL_KEY])
            ? [chatOn[CHAT_ON_IMAGE_URL_KEY]]
            : [];

      if (resolvedImageURLs.length === 0) {
        return {
          type,
          renderText: text,
          captionText: "",
          imageURLs: [],
          videoURLs: [],
          audioURLs: [],
          mediaMeta: {},
        };
      }

      return {
        type,
        renderText: "",
        captionText:
          text.trim() && !CHAT_ON_FILE_NAME_REGEX.test(text.trim()) ? text : "",
        imageURLs: resolvedImageURLs,
        videoURLs: [],
        audioURLs: [],
        mediaMeta: {},
      };
    }
    case "gif": {
      const resolvedImageURLs = isChatOnMediaUrl(chatOn[CHAT_ON_GIF_URL_KEY])
        ? [chatOn[CHAT_ON_GIF_URL_KEY]]
        : [];
      const gifTitle = chatOn[CHAT_ON_GIF_TITLE_KEY];

      if (resolvedImageURLs.length === 0) {
        return {
          type,
          renderText: text,
          captionText: "",
          imageURLs: [],
          videoURLs: [],
          audioURLs: [],
          mediaMeta: {},
        };
      }

      return {
        type,
        renderText: "",
        captionText:
          text.trim() && text !== gifTitle && text !== CHAT_ON_DEFAULT_GIF_TEXT
            ? text
            : "",
        imageURLs: resolvedImageURLs,
        videoURLs: [],
        audioURLs: [],
        mediaMeta: {},
      };
    }
    case "sticker": {
      const resolvedImageURLs = isChatOnMediaUrl(chatOn[CHAT_ON_GIF_URL_KEY])
        ? [chatOn[CHAT_ON_GIF_URL_KEY]]
        : [];

      return {
        type,
        renderText: resolvedImageURLs.length === 0 ? text : "",
        captionText: "",
        imageURLs: resolvedImageURLs,
        videoURLs: [],
        audioURLs: [],
        mediaMeta: {},
      };
    }
    case "video": {
      const resolvedVideoURLs =
        videoURLs.length > 0
          ? videoURLs
          : isChatOnMediaUrl(chatOn[CHAT_ON_VIDEO_URL_KEY])
            ? [chatOn[CHAT_ON_VIDEO_URL_KEY]]
            : [];

      if (resolvedVideoURLs.length === 0) {
        return {
          type,
          renderText: text,
          captionText: "",
          imageURLs: [],
          videoURLs: [],
          audioURLs: [],
          mediaMeta: {},
        };
      }

      return {
        type,
        renderText: "",
        captionText:
          text.trim() && text !== CHAT_ON_DEFAULT_VIDEO_TEXT ? text : "",
        imageURLs: [],
        videoURLs: resolvedVideoURLs,
        audioURLs: [],
        mediaMeta: getChatOnVideoMediaMeta(
          resolvedVideoURLs[0]!,
          width,
          height,
          duration,
        ),
      };
    }
    case "audio": {
      const resolvedAudioURLs =
        audioURLs.length > 0
          ? audioURLs
          : isChatOnMediaUrl(chatOn[CHAT_ON_AUDIO_URL_KEY])
            ? [chatOn[CHAT_ON_AUDIO_URL_KEY]]
            : [];

      if (resolvedAudioURLs.length === 0) {
        return {
          type,
          renderText: text,
          captionText: "",
          imageURLs: [],
          videoURLs: [],
          audioURLs: [],
          mediaMeta: {},
        };
      }

      return {
        type,
        renderText: "",
        captionText: text.trim() && text !== "audio" ? text : "",
        imageURLs: [],
        videoURLs: [],
        audioURLs: resolvedAudioURLs,
        mediaMeta: getChatOnAudioMediaMeta(resolvedAudioURLs[0]!, duration),
      };
    }
    case "file":
      return {
        type,
        renderText: getChatOnFileFallbackText(text, chatOn),
        captionText: "",
        imageURLs: [],
        videoURLs: [],
        audioURLs: [],
        mediaMeta: {},
      };
    case "reaction":
    case "system":
    case "text":
    case "tip":
    default:
      return {
        type,
        renderText:
          type === "system"
            ? text || chatOn[CHAT_ON_SYSTEM_ACTION_KEY] || ""
            : text,
        captionText: "",
        imageURLs: [],
        videoURLs: [],
        audioURLs: [],
        mediaMeta: {},
      };
  }
};

export const sanitizeMessagingGroupDisplayName = (
  name?: string | null,
): string => {
  if (!name) {
    return "";
  }

  return name
    .replace(MESSAGE_GROUP_NAME_ESCAPED_CONTROL_CHAR_REGEX, " ")
    .replace(MESSAGE_GROUP_NAME_CONTROL_CHAR_REGEX, " ")
    .replace(MESSAGE_GROUP_NAME_WHITESPACE_REGEX, " ")
    .trim();
};

export const getThreadName = (
  thread: ThreadFragment,
  readerPublicKey: string,
) => {
  if (thread.isGroupChatMessage) {
    if (
      thread.accessGroupKeyName &&
      !isAutoGeneratedGroupChatName(thread.accessGroupKeyName)
    ) {
      const sanitizedGroupName = sanitizeMessagingGroupDisplayName(
        thread.accessGroupKeyName,
      );

      if (sanitizedGroupName) {
        return sanitizedGroupName;
      }
    }

    const members = getThreadMembers(thread);

    return members.map((x) => getUserName({ account: x })).join(", ");
  } else {
    const message = thread.messages.nodes?.[0];

    if (!message) {
      throw new Error(
        `No messages found for thread id: ${thread.threadIdentifier}`,
      );
    }

    const account =
      message.sender?.publicKey === readerPublicKey
        ? message.receiver
        : message.sender;

    if (!account) {
      throw new Error(
        `Could not determine account name for thread id: ${thread.threadIdentifier}`,
      );
    }

    return `${getUserName({ account })}`;
  }
};

export const getThreadMembers = (
  thread: ThreadFragment,
): (ThreadMemberFragment & { encryptedKey?: string })[] => {
  const lexicalSort = (a: ThreadMemberFragment, b: ThreadMemberFragment) => {
    const aUsername = getUserName({ account: a });
    const bUsername = getUserName({ account: b });
    return aUsername.localeCompare(bUsername);
  };
  if (thread.isGroupChatMessage) {
    const members =
      (thread.participants.nodes || [])
        .concat(thread.me.nodes || [])
        ?.reduce<Account[]>((acc, curr) => {
          if (!curr?.participant) return acc as Account[];

          return [...acc, curr.participant as Account];
        }, []) ?? [];

    if (!members.length) {
      throw new Error(
        `Could not determine thread members for thread id: ${thread.threadIdentifier}`,
      );
    }

    members.sort(lexicalSort);

    return members;
  } else {
    const message = thread.messages.nodes?.[0];

    if (!message) {
      throw new Error(
        `No messages found for thread id: ${thread.threadIdentifier}`,
      );
    }

    if (!(message.sender && message.receiver)) {
      throw new Error(
        `Could not determine thread members for thread id: ${thread.threadIdentifier}`,
      );
    }

    const members = [message.sender, message.receiver];
    members.sort(lexicalSort);

    return members;
  }
};

export const decryptMessage = async (
  message: DeepPartial<MessageFragment>,
  readerPublicKey: string,
  decryptionSeedHex?: string,
): Promise<{
  text: string;
  videoURLs: string[];
  imageURLs: string[];
  audioURLs: string[];
  chatOn: ChatOnData;
}> => {
  const result = {
    text: "",
    videoURLs: [],
    imageURLs: [],
    audioURLs: [],
    chatOn: {},
  };

  if (
    message.extraData?.encryptedWithSharedPrivateKey === "false" &&
    readerPublicKey === message.sender?.publicKey
  ) {
    const {
      currentUser: {
        // @ts-expect-error it's fine
        primaryDerivedKey: { messagingPrivateKey },
      },
    } = await identity.snapshot();
    result.text = await decrypt(
      messagingPrivateKey,
      message.extraData.encryptedToSenderMessageText,
    );
    result.chatOn = await resolveChatOnData(message, {
      mode: "shared-private-key",
      messagingPrivateKey,
    });
    return result;
  }

  let publicDecryptionKey = "";

  if (message.extraData?.unencrypted === "true") {
    const maybeTextBytes =
      message.encryptedText && hexToBytes(message.encryptedText);
    const maybeImageURLBytes =
      message.extraData.encryptedImageURLs &&
      hexToBytes(message.extraData.encryptedImageURLs);
    const maybeVideoURLsBytes =
      message.extraData.encryptedVideoURLs &&
      hexToBytes(message.extraData.encryptedVideoURLs);
    const maybeAudioURLsBytes =
      message.extraData.encryptedAudioURLs &&
      hexToBytes(message.extraData.encryptedAudioURLs);

    if (maybeTextBytes) {
      result.text = new TextDecoder().decode(maybeTextBytes);
    }
    if (maybeImageURLBytes) {
      result.imageURLs = JSON.parse(
        new TextDecoder().decode(maybeImageURLBytes),
      );
    }
    if (maybeVideoURLsBytes) {
      result.videoURLs = JSON.parse(
        new TextDecoder().decode(maybeVideoURLsBytes),
      );
    }
    if (maybeAudioURLsBytes) {
      result.audioURLs = JSON.parse(
        new TextDecoder().decode(maybeAudioURLsBytes),
      );
    }

    result.chatOn = await resolveChatOnData(message, {
      mode: "unencrypted",
    });
    return result;
  }

  // We may not have this if the message was sent unencrypted which should have been handled above. If we get here and
  // we don't have it, then something is wrong.
  if (!decryptionSeedHex) {
    throw new Error("Decryption seed hex is required to decrypt message.");
  }

  if (message.isGroupChatMessage) {
    if (!message.sender) {
      throw new Error("Could not resolve sender for message");
    }

    const senderMessagingPublicKey =
      message.sender.accessGroupsOwned?.nodes?.[0]?.accessGroupPublicKey;

    if (!senderMessagingPublicKey) {
      throw new Error("Could not resolve messaging public key sender.");
    }

    publicDecryptionKey = senderMessagingPublicKey;
  } else {
    if (!message.sender) {
      throw new Error(`Could not resolve sender for message.`);
    }
    if (!message.receiver) {
      throw new Error("Could not resolve receiver for message.");
    }
    const decryptionKey =
      readerPublicKey === message.sender.publicKey
        ? message.receiver.accessGroupsOwned?.nodes?.[0]?.accessGroupPublicKey
        : message.sender.accessGroupsOwned?.nodes?.[0]?.accessGroupPublicKey;

    if (!decryptionKey) {
      throw new Error("Could not resolve public decryption key for message");
    }

    publicDecryptionKey = decryptionKey;
  }

  if (!publicDecryptionKey) {
    throw new Error("Could not resolve public decryption key for message");
  }

  if (message.encryptedText) {
    result.text = await decryptChatMessage(
      decryptionSeedHex,
      publicDecryptionKey,
      message.encryptedText,
    );
  }

  if (message.extraData?.encryptedImageURLs) {
    result.imageURLs = JSON.parse(
      await decryptChatMessage(
        decryptionSeedHex,
        publicDecryptionKey,
        message.extraData.encryptedImageURLs,
      ),
    );
  }

  if (message.extraData?.encryptedVideoURLs) {
    result.videoURLs = JSON.parse(
      await decryptChatMessage(
        decryptionSeedHex,
        publicDecryptionKey,
        message.extraData.encryptedVideoURLs,
      ),
    );
  }

  if (message.extraData?.encryptedAudioURLs) {
    result.audioURLs = JSON.parse(
      await decryptChatMessage(
        decryptionSeedHex,
        publicDecryptionKey,
        message.extraData.encryptedAudioURLs,
      ),
    );
  }

  result.chatOn = await resolveChatOnData(message, {
    mode: "chat",
    decryptionSeedHex,
    publicDecryptionKey,
  });

  return result;
};

export interface DecryptedChatMessage {
  text: string;
  // isError is true if the message could not be decrypted.
  isError: boolean;
  sender?: PartialAccount | null;

  // TODO: we need to add tip status data here..
  paidMessageData?: PaidMessageData | null;
  timestamp: string;
  hasPayment: boolean;
  isSender: boolean;
}

export const getMessagePreviewParts = ({
  isGroupChat,
  previewMessage,
}: {
  isGroupChat: boolean;
  previewMessage: DecryptedChatMessage;
}): {
  prefix?: string;
  text: string;
} => {
  const text = previewMessage.text;

  if (!text) {
    return { text: "" };
  }

  if (previewMessage.isSender) {
    return isGroupChat ? { prefix: "You", text } : { text: `You: ${text}` };
  }

  if (!isGroupChat) {
    return { text };
  }

  const sender = previewMessage.sender;

  if (!sender?.publicKey && !sender?.username) {
    return { text };
  }

  const senderName = getUserDisplayName({
    extraData: sender.extraData,
    publicKey: sender.publicKey,
    username: sender.username,
    customPrefix: "",
  });

  return { prefix: senderName, text };
};

export const getMessagePreviewText = ({
  isGroupChat,
  previewMessage,
}: {
  isGroupChat: boolean;
  previewMessage: DecryptedChatMessage;
}) => {
  const { prefix, text } = getMessagePreviewParts({
    isGroupChat,
    previewMessage,
  });

  return prefix ? `${prefix}: ${text}` : text;
};

export interface ThreadMetadata {
  previewMessage: DecryptedChatMessage;
  members: ThreadMemberFragment[];
  me: ThreadMemberFragment;
  displayName: string;
  decryptionSeedHex: string;
  initiatorPublicKey: string;
  isSpam: boolean;
  requiredPaymentAmountUsdCents: number;
  tier?: FocusSubscription;
}
export interface ThreadItem {
  thread: FilterableMessageThreadFragment;
  metadata: ThreadMetadata;
}
export const getThreadMetadata = async (
  filteredThread: FilterableMessageThreadFragment,
  readerPublicKey: string,
): Promise<ThreadMetadata> => {
  const thread = filteredThread.thread;
  if (!thread) {
    throw new Error("No thread mapping found.");
  }
  const members = getThreadMembers(thread);
  const displayName = getThreadName(thread, readerPublicKey);
  let decryptionSeedHex = "";

  if (thread.isGroupChatMessage) {
    const encryptedCurrentUserKey =
      thread.accessGroupMembers.nodes?.[0]?.encryptedKey;

    if (!encryptedCurrentUserKey) {
      throw new Error(
        `Could not find decryption key for current user for thread id: ${thread.threadIdentifier}`,
      );
    }

    const { seedHex } = await identity.decryptAccessGroupKeyPair(
      encryptedCurrentUserKey.slice(2),
    );

    decryptionSeedHex = seedHex;
  } else {
    const { currentUser } = await identity.snapshot();
    const seedHex = currentUser?.primaryDerivedKey?.messagingPrivateKey;

    if (!seedHex) {
      throw new Error(
        "Could not find DM messaging private key for current user.",
      );
    }

    decryptionSeedHex = seedHex;
  }

  if (decryptionSeedHex === "") {
    throw new Error(
      `Could not find private decryption key for thread id: ${thread.threadIdentifier}`,
    );
  }

  const previewMessage: DecryptedChatMessage = {
    text: "",
    isError: false,
    timestamp: "",
    hasPayment: false,
    paidMessageData: null,
    isSender: false,
  };
  try {
    const latestMessage = thread.messages.nodes?.[0];

    if (!latestMessage) {
      throw new Error(
        `No messages found for thread id: ${thread.threadIdentifier}`,
      );
    }

    const paidMessageData = parsePaidMessageData(latestMessage);
    previewMessage.paidMessageData = paidMessageData;
    previewMessage.timestamp = latestMessage.timestamp;
    previewMessage.hasPayment =
      Number(latestMessage?.paymentAmountUsdCents ?? "0") > 0;
    previewMessage.isSender =
      latestMessage.sender?.publicKey === readerPublicKey;
    previewMessage.sender = latestMessage.sender ?? null;
    const decryptedContent = await decryptMessage(
      latestMessage,
      readerPublicKey,
      decryptionSeedHex,
    );
    previewMessage.text = decryptedContent.text;
  } catch (e) {
    previewMessage.text = `Failed to decrypt message: ${parseError(e)}`;
    previewMessage.isError = true;
    if (process.env.NODE_ENV !== "production") {
      console.error(e);
    }
  }

  const initiatorPublicKey = filteredThread.initiatorPublicKey;

  if (!initiatorPublicKey) {
    throw new Error(
      `Could not find initiator public key for thread id: ${thread.threadIdentifier}`,
    );
  }

  const me = filteredThread.thread?.me.nodes?.[0]?.participant;
  if (!me) {
    throw new Error(
      `Could not find current user in thread id: ${thread.threadIdentifier}`,
    );
  }

  return {
    decryptionSeedHex,
    previewMessage,
    members,
    me,
    displayName,
    initiatorPublicKey,
    isSpam: filteredThread?.isSpam ?? false,
    requiredPaymentAmountUsdCents: Number(
      filteredThread?.requiredPaymentAmountUsdCents ?? "0",
    ),
  };
};

export const getDefaultMessagingKey = (threadMember: ThreadMemberFragment) => {
  return threadMember.accessGroupsOwned?.nodes?.find(
    (n) => n?.accessGroupKeyName === DEFAULT_MESSAGING_GROUP_KEY_NAME,
  );
};

const AUTO_GEN_GROUP_CHAT_NAME_PREFIX = "focus-chat-";

/**
 * Generate a unique, deterministic group chat name with a fixed length of 32 based on the group members' public keys.
 * We don't use the member key string directly because group chat names have a max length of 32 characters.
 * https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/digest#converting_a_digest_to_a_hex_string
 * @returns
 */
export const generateNewGroupChatName = async (publicKeys: string[]) => {
  const sortedPublicKeys = publicKeys.sort().join("");
  const hashBytes = await window.crypto.subtle.digest(
    "SHA-1", // SHA-1 is fine here because we're not using it for security purposes.
    new TextEncoder().encode(sortedPublicKeys),
  );
  const hashHex = Array.from(new Uint8Array(hashBytes))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

  return `${AUTO_GEN_GROUP_CHAT_NAME_PREFIX}${hashHex}`.slice(0, 32);
};

export const isAutoGeneratedGroupChatName = (name: string) => {
  return name.startsWith(AUTO_GEN_GROUP_CHAT_NAME_PREFIX) && name.length === 32;
};

export const buildDMThreadIdentifier = (
  publicKey1: string,
  publicKey2: string,
) => {
  const prefix =
    publicKey1 < publicKey2
      ? `${publicKey1}-${publicKey2}`
      : `${publicKey2}-${publicKey1}`;

  return `${prefix}-${DEFAULT_MESSAGING_GROUP_KEY_NAME}-${DEFAULT_MESSAGING_GROUP_KEY_NAME}-false`;
};

export const buildGroupChatThreadIdentifier = (
  accessGroupOwnerPublicKey: string,
  accessGroupKeyName: string,
  accessGroupPublicKey: string,
) => {
  return `${accessGroupOwnerPublicKey}-${accessGroupKeyName}-${accessGroupPublicKey}-true`;
};

// TODO: export this from deso-protocol
export type SendNewMessageParams = TypeWithOptionalFeesAndExtraData<
  Omit<SendNewMessageRequest, "TimestampNanosString">
>;

// Check for new messages every second
export const MESSAGE_POLL_INTERVAL = getPollInterval(10000);

export interface MessageGatedContentSchema {
  PayToUnlockAccessGroupKeyName: string;
  PayToUnlockAccessGroupOwnerPublicKey: string;
  PayToUnlockAccessGroupPublicKey: string;
  PayToUnlockEncryptedText: string;
  PayToUnlockEncryptedImageURLs: string;
  PayToUnlockEncryptedVideoURLs: string;
  PayToUnlockEncryptedAudioURLs: string;
  PayToUnlockPriceUsdCents: number;
  PayToUnlockBlurHash?: string;
}
export const parseMessageGatedContent = (
  messageExtraData: Maybe<Record<string, string>>,
): MessageGatedContentSchema | null => {
  if (
    !(
      messageExtraData &&
      messageExtraData.PayToUnlockAccessGroupKeyName &&
      messageExtraData.PayToUnlockAccessGroupOwnerPublicKey &&
      messageExtraData.PayToUnlockAccessGroupPublicKey &&
      messageExtraData.PayToUnlockPriceUsdCents &&
      (messageExtraData.PayToUnlockEncryptedText ||
        messageExtraData.PayToUnlockEncryptedImageURLs ||
        messageExtraData.PayToUnlockEncryptedVideoURLs ||
        messageExtraData.PayToUnlockEncryptedAudioURLs)
    )
  ) {
    return null;
  }

  const {
    PayToUnlockAccessGroupKeyName,
    PayToUnlockAccessGroupOwnerPublicKey,
    PayToUnlockAccessGroupPublicKey,
    PayToUnlockEncryptedText,
    PayToUnlockEncryptedImageURLs,
    PayToUnlockEncryptedVideoURLs,
    PayToUnlockEncryptedAudioURLs,
    PayToUnlockPriceUsdCents,
    PayToUnlockBlurHash,
  } = messageExtraData;

  return {
    PayToUnlockAccessGroupKeyName,
    PayToUnlockAccessGroupOwnerPublicKey,
    PayToUnlockAccessGroupPublicKey,
    PayToUnlockEncryptedText,
    PayToUnlockEncryptedImageURLs: PayToUnlockEncryptedImageURLs ?? "",
    PayToUnlockEncryptedVideoURLs: PayToUnlockEncryptedVideoURLs ?? "",
    PayToUnlockEncryptedAudioURLs: PayToUnlockEncryptedAudioURLs ?? "",
    PayToUnlockPriceUsdCents: Number(PayToUnlockPriceUsdCents),
    PayToUnlockBlurHash,
  };
};

export const buildMessagePaginationCursor = (message: MessageFragment) => {
  if (!message) {
    throw new Error("Could not extract message data from message fragment");
  }

  const [, decodedId] = JSON.parse(atob(message.id));
  return btoa(
    JSON.stringify(["timestamp_desc", [message.timestamp, decodedId]]),
  );
};

export const buildClientSideDMThread = async (
  sender: PartialAccount,
  recipient: PartialAccount,
  apollo: Pick<ApolloClient, "query">,
): Promise<ThreadItem> => {
  if (!sender.publicKey) {
    throw new Error("Sender public key was not found.");
  }

  if (!recipient.publicKey) {
    throw new Error("Recipient public key was not found.");
  }

  const [
    { data: recipientDefaultMessagingGroupData },
    { data: senderDefaultMessagingGroupData },
  ] = await Promise.all([
    apollo.query({
      query:
        AccessGroupByAccessGroupOwnerPublicKeyAndAccessGroupKeyNameDocument,
      variables: {
        accessGroupOwnerPublicKey: recipient.publicKey,
        accessGroupKeyName: DEFAULT_MESSAGING_GROUP_KEY_NAME,
      },
    }),
    apollo.query({
      query:
        AccessGroupByAccessGroupOwnerPublicKeyAndAccessGroupKeyNameDocument,
      variables: {
        accessGroupOwnerPublicKey: sender.publicKey,
        accessGroupKeyName: DEFAULT_MESSAGING_GROUP_KEY_NAME,
      },
    }),
  ]);
  const recipientDefaultMessagingGroup =
    recipientDefaultMessagingGroupData?.accessGroupByAccessGroupOwnerPublicKeyAndAccessGroupKeyName;
  const recipientThreadMember = {
    publicKey: recipient.publicKey,
    username: recipient.username,

    followerCounts: {
      totalFollowers: 0,
      totalWhaleFollowers: 0,
    },
    followingCounts: {
      totalFollowing: 0,
      totalWhaleFollowing: 0,
    },
    accessGroupsOwned: {
      nodes: recipientDefaultMessagingGroup
        ? [recipientDefaultMessagingGroup]
        : [],
    },
  };
  const senderDefaultMessagingGroup =
    senderDefaultMessagingGroupData?.accessGroupByAccessGroupOwnerPublicKeyAndAccessGroupKeyName;
  const senderThreadMember = {
    publicKey: sender.publicKey,
    username: sender.username,
    followerCounts: {
      totalFollowers: 0,
      totalWhaleFollowers: 0,
    },
    followingCounts: {
      totalFollowing: 0,
      totalWhaleFollowing: 0,
    },
    accessGroupsOwned: {
      nodes: senderDefaultMessagingGroup ? [senderDefaultMessagingGroup] : [],
    },
  };

  const { currentUser } = await identity.snapshot();
  const decryptionSeedHex = currentUser?.primaryDerivedKey?.messagingPrivateKey;

  if (!decryptionSeedHex) {
    throw new Error("Could not find messaging private key for current user.");
  }

  if (!sender.publicKey) {
    throw new Error("Sender public key was not found.");
  }

  if (!recipient.publicKey) {
    throw new Error("Recipient public key was not found.");
  }

  return {
    thread: {
      threadIdentifier: buildDMThreadIdentifier(
        sender.publicKey,
        recipient.publicKey,
      ),
      initiatorPublicKey: sender.publicKey,
      requiredPaymentAmountUsdCents: "0",
      isSpam: false,
      thread: {
        threadIdentifier: buildDMThreadIdentifier(
          sender.publicKey,
          recipient.publicKey,
        ),
        isGroupChatMessage: false,
        accessGroupKeyName: "",
        accessGroupMembers: {
          nodes: [],
        },
        messages: {
          nodes: [],
        },
        unreadMessagesCount: {
          nodes: [],
        },
        participants: {
          totalCount: 2,
          nodes: [],
        },
        me: {
          nodes: [],
        },
      },
    },
    metadata: {
      initiatorPublicKey: sender.publicKey,
      previewMessage: {
        text: "",
        isError: false,
        sender: senderThreadMember,
        isSender: true,
        hasPayment: false,
        paidMessageData: null,
        timestamp: new Date().toISOString(),
      },
      isSpam: false,
      requiredPaymentAmountUsdCents: 0,
      me: senderThreadMember,
      members: [recipientThreadMember, senderThreadMember],
      displayName: `${getUserName({ account: recipient })}`,
      decryptionSeedHex,
    },
  };
};

export const isDMThreadId = (threadId: string) => {
  return threadId.endsWith("-false");
};

export const buildClientSideSubscriptionThread = ({
  displayName,
  sender,
  receiver,
  tier,
  decryptionSeedHex,
}: {
  displayName: string;
  sender: PartialAccount;
  receiver: PartialAccount;
  tier: FocusSubscription;
  decryptionSeedHex: string;
}) => {
  const threadIdentifier = buildGroupChatThreadIdentifier(
    tier.accessGroup!.accessGroupOwnerPublicKey!,
    tier.accessGroupKeyName!,
    tier.accessGroup!.accessGroupPublicKey!,
  );
  const normalizedTier = {
    ...tier,
    accessGroupOwnerPublicKey:
      tier.accessGroupOwnerPublicKey ??
      tier.accessGroup?.accessGroupOwnerPublicKey ??
      null,
  } as FocusSubscription;

  return {
    thread: {
      threadIdentifier,
      initiatorPublicKey: null,
      requiredPaymentAmountUsdCents: "0",
      isSpam: false,
      isGroupChatMessage: true,
      thread: {
        threadIdentifier,
        isGroupChatMessage: true,
        accessGroupKeyName: tier.accessGroupKeyName,
        accessGroupOwnerPublicKey: tier.accessGroup?.accessGroupOwnerPublicKey,
        accessGroupMembers: {
          nodes: [],
        },
        messages: {
          nodes: [],
        },
        unreadMessagesCount: {
          nodes: [],
        },
        participants: {
          totalCount: 2,
          nodes: [],
        },
        me: {
          nodes: [],
        },
      },
    },
    metadata: {
      initiatorPublicKey: null,
      previewMessage: {
        text: "",
        isError: false,
        sender,
        isSender: true,
        hasPayment: false,
        paidMessageData: null,
        timestamp: new Date().toISOString(),
      },
      isSpam: false,
      requiredPaymentAmountUsdCents: 0,
      me: sender,
      members: [receiver],
      displayName,
      decryptionSeedHex,
      tier: normalizedTier,
    },
  };
};

export const isSubscriptionThread = (
  threadIdentifier: string | null | undefined,
) => !!threadIdentifier?.includes("focus-sub-tier");

/**
 * Each association type represents a progressively more granular level of settings.
 * For example, from most broad to most specific:
 * - PAID_MESSAGING_SETTINGS
 * - CUSTOM_MESSAGING_USER_SETTINGS
 * - CUSTOM_MESSAGING_THREAD_SETTINGS
 */
export const CUSTOM_THREAD_SETTINGS_ASSOCIATION_TYPE =
  "CUSTOM_MESSAGING_THREAD_SETTINGS";
export const CUSTOM_MESSAGE_USER_SETTINGS_ASSOCIATION_TYPE =
  "CUSTOM_MESSAGING_USER_SETTINGS";
export const PAID_MESSAGING_SETTINGS_ASSOCIATION_TYPE =
  "PAID_MESSAGING_SETTINGS";

export const NOTIFICATION_SETTINGS_ASSOCIATION_TYPE = "NOTIFICATION_SETTINGS";
export const PAY_TO_REPOST_SETTINGS_ASSOCIATION_TYPE = "PAY_TO_REPOST_SETTINGS";

export interface MessageSettings {
  feePerMessageUsdCents: number;
  followingFeePerMessageUsdCents?: number;
  canViewerMessageForFree?: boolean;
}

export const parseMessageSettings = (
  associationExtraData: Maybe<Record<string, string>>,
): MessageSettings | null => {
  if (!(associationExtraData && associationExtraData.FeePerMessageUsdCents)) {
    return null;
  }

  return {
    feePerMessageUsdCents: Number(associationExtraData.FeePerMessageUsdCents),
    followingFeePerMessageUsdCents: Number(
      associationExtraData.FollowingFeePerMessageUsdCents,
    ),
  };
};

interface PaidMessageData {
  PaidMessageDerivedPublicKey: string;
  PaymentAmountUsdCents: number;
  PaymentStatus: string;
}
export const parsePaidMessageData = (
  message: DeepPartial<MessageFragment>,
): PaidMessageData | null => {
  if (!message.extraData.PaidMessageDerivedPublicKey) return null;

  const { PaidMessageDerivedPublicKey, PaymentAmountUsdCents } =
    message.extraData;

  return {
    PaidMessageDerivedPublicKey,
    PaymentAmountUsdCents: Number(PaymentAmountUsdCents),
    PaymentStatus: message.paymentStatus ?? "",
  };
};

export enum ThreadTabValue {
  Inbox = "Inbox",
  Spam = "Spam",
  Subs = "Subscriptions",
}

export enum ThreadSetting {
  Inbox = 0,
  Spam = -1,
}

export enum UserSetting {
  Allowlist = 0,
  Denylist = -1,
}

export const createCustomThreadSetting = (
  threadIdentifier: string,
  viewerPublicKey: string,
  feePerMessageUsdCents: number,
) => {
  return createUserAssociation({
    AssociationType: CUSTOM_THREAD_SETTINGS_ASSOCIATION_TYPE,
    AssociationValue: threadIdentifier,
    TargetUserPublicKeyBase58Check: viewerPublicKey,
    TransactorPublicKeyBase58Check: viewerPublicKey,
    AppPublicKeyBase58Check: process.env.NEXT_PUBLIC_FOCUS_APP_PUBLIC_KEY,
    ExtraData: {
      FeePerMessageUsdCents: feePerMessageUsdCents.toString(),
      Version: "0",
    },
  });
};

export const createCustomMessageUserSetting = (
  viewerPublicKey: string,
  userPublicKey: string,
  feePerMessageUsdCents: number,
) => {
  return createUserAssociation({
    AssociationType: CUSTOM_MESSAGE_USER_SETTINGS_ASSOCIATION_TYPE,
    AssociationValue: userPublicKey,
    TargetUserPublicKeyBase58Check: viewerPublicKey,
    TransactorPublicKeyBase58Check: viewerPublicKey,
    AppPublicKeyBase58Check: process.env.NEXT_PUBLIC_FOCUS_APP_PUBLIC_KEY,
    ExtraData: {
      FeePerMessageUsdCents: feePerMessageUsdCents.toString(),
      Version: "0",
    },
  });
};

export const NUM_HOURS_FOR_PAID_MESSAGE_WINDOW = 72;
export const has72HoursPassed = (timestamp: string) => {
  return (
    new Date(timestamp).getTime() <=
    Date.now() - NUM_HOURS_FOR_PAID_MESSAGE_WINDOW * 60 * 60 * 1000
  );
};

export const getNiceErrorMessage = (rawError: string) => {
  if (
    rawError.includes(
      "User does not have enough balance in any of the provided currencies to make payment",
    )
  ) {
    return "Sender does not have enough money to process payment.";
  }

  if (rawError.includes("Error getting derived key from db")) {
    return "Sender payment derived key not found.";
  }

  if (rawError.includes("RuleErrorDerivedKeyDAOCoinOperationNotAuthorized")) {
    return `Sender payment derived key not authorized to transfer a ${DESO_TOKEN_NAME}.`;
  }

  // We just fall back to the raw error if we don't have a nice error message match.
  return rawError;
};
